// Hotel-Edition module "Instagram-Reels" (PLAN.md §7.4, part B2): Instagram reels as 9:16 phone frames with
// poster and play badge; the detail plays the video (<video playsInline controls preload="none" poster>, via the
// Range-capable /objects route) next to caption, hashtags, Rubrik and date, with edit text / delete while allowed
// and "Freigeben" leading to the weekly approval (Mike B4). Reels never go to the planner: the team publishes them.
import { Instagram, Clapperboard } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { statusConfig } from "@/lib/status-config"
import { HotelPostDialog, postDateLabel } from "@/components/instagram/HotelPostDialog"
import { useHotelPostList } from "@/components/instagram/useHotelPostList"
import { REEL_EDIT_HINT } from "@/components/instagram/hotelPosts"
import { ReelPhoneFrame } from "@/components/instagram/ReelPhoneFrame"

/** Status badge of a reel: an approved reel is published by the team, not scheduled by the planner. */
function reelStatus(post: { status: string; publishStatus?: string | null }) {
  if (post.publishStatus === "manual") {
    return { label: "Freigegeben, Ihr Team veröffentlicht", color: statusConfig.approved.color }
  }
  return statusConfig[post.status as keyof typeof statusConfig] || statusConfig.pending
}

export default function InstagramReels() {
  const { posts, total, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, selected, setSelected } =
    useHotelPostList("instagram-reels", { platform: "instagram", mediaFormat: "reel" })

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="page-instagram-reels">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Instagram className="h-8 w-8 text-pink-600" />
          Instagram-Reels
        </h1>
        <p className="text-muted-foreground mt-2">
          Ihre Reels für Instagram. {REEL_EDIT_HINT}
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="aspect-[9/16] rounded-[2rem]" />)}
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">Die Reels konnten nicht geladen werden. Bitte laden Sie die Seite neu.</CardContent>
        </Card>
      ) : posts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Clapperboard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Noch keine Instagram-Reels</h3>
            <p className="text-muted-foreground">Sobald Ihr Team die Reels der Woche vorbereitet hat, erscheinen sie hier.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground" data-testid="text-result-count">{posts.length} von {total} Reels</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {posts.map((post) => {
              const status = reelStatus(post)
              return (
                <button
                  key={post.id}
                  type="button"
                  className="flex flex-col justify-start text-left gap-2 rounded-xl p-1 hover:bg-muted/50 transition-colors"
                  onClick={() => setSelected(post)}
                  aria-label={`Reel ansehen: ${post.title || "Ohne Titel"}`}
                  data-testid={`card-instagram-reel-${post.id}`}
                >
                  <ReelPhoneFrame mode="preview" posterUrl={post.posterUrl} videoUrl={post.videoUrl} title={post.title} />
                  <div className="space-y-1 px-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground truncate">{post.pillarName}</span>
                    </div>
                    <Badge className={status.color}>{status.label}</Badge>
                    <p className="text-sm line-clamp-2">{post.postContent}</p>
                    <p className="text-xs text-muted-foreground">{postDateLabel(post)}</p>
                  </div>
                </button>
              )
            })}
          </div>
          {hasNextPage && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? "Lädt…" : "Mehr laden"}
              </Button>
            </div>
          )}
        </>
      )}

      <HotelPostDialog
        post={selected}
        open={!!selected}
        onOpenChange={(open) => { if (!open) setSelected(null) }}
        media={selected ? (
          <div className="w-full max-w-[260px] mx-auto">
            {/* key: a new reel gets a fresh <video> (no stale buffer from the previous one) */}
            <ReelPhoneFrame
              key={selected.id}
              mode="player"
              posterUrl={selected.posterUrl}
              videoUrl={selected.videoUrl}
              title={selected.title}
            />
          </div>
        ) : undefined}
      />
    </div>
  )
}
